import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Loader2, Fuel, Clock, Thermometer, Droplets, Wifi, Timer, Gauge, Signal, Zap } from "lucide-react";

const STATUS = {
  ok: { t: "Óptimo", ring: "#10b981", badge: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200", glow: "shadow-emerald-500/20" },
  bajo: { t: "Bajo", ring: "#f59e0b", badge: "bg-amber-50 text-amber-700 ring-1 ring-amber-200", glow: "shadow-amber-500/20" },
  critico: { t: "Crítico", ring: "#ef4444", badge: "bg-red-50 text-red-700 ring-1 ring-red-200", glow: "shadow-red-500/30" },
  sin_lectura: { t: "Sin lectura", ring: "#94a3b8", badge: "bg-slate-100 text-slate-600 ring-1 ring-slate-200", glow: "shadow-slate-400/10" },
  error: { t: "Sin conexión", ring: "#94a3b8", badge: "bg-slate-100 text-slate-600 ring-1 ring-slate-200", glow: "shadow-slate-400/10" },
};
const fmtTs = (t) => (t ? new Date(t).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }) : "—");

function Kpi({ label, value, icon: Icon, accent, tone = "slate" }) {
  const tones = {
    slate: "text-slate-900",
    red: "text-red-600",
    emerald: "text-emerald-600",
    amber: "text-amber-600",
  };
  return (
    <div
      data-testid={`cuby-kpi-${label}`}
      className="group relative overflow-hidden bg-white rounded-3xl border border-slate-200/70 shadow-sm p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
    >
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-slate-50 group-hover:scale-125 transition-transform duration-500" />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
          <p className={`text-4xl font-extrabold mt-2 tabular-nums ${accent ? tones.red : tones[tone]}`}>{value}</p>
        </div>
        <div className={`p-2.5 rounded-2xl bg-slate-50 ${accent ? "text-red-500" : "text-slate-400"}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function GasGauge({ level, ring }) {
  const R = 78;
  const C = 2 * Math.PI * R;
  const pct = level == null ? 0 : Math.max(0, Math.min(100, level));
  const offset = C - (pct / 100) * C;
  return (
    <div className="relative w-[220px] h-[220px] mx-auto">
      <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
        <circle cx="100" cy="100" r={R} fill="none" stroke="#f1f5f9" strokeWidth="16" />
        <circle
          cx="100" cy="100" r={R} fill="none" stroke={ring} strokeWidth="16" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 1s cubic-bezier(0.4,0,0.2,1), stroke 0.4s" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-6xl font-extrabold text-slate-900 tabular-nums leading-none">{level == null ? "—" : level}</span>
        <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mt-2">% gas</span>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, color = "text-slate-500" }) {
  return (
    <div className="flex flex-col items-center gap-2.5 flex-1 py-5 rounded-2xl transition-colors hover:bg-slate-50">
      <Icon className={`w-8 h-8 ${color}`} />
      <p className="text-3xl font-extrabold text-slate-900 tabular-nums leading-none">{value}</p>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    </div>
  );
}

function DeviceCard({ d }) {
  const s = STATUS[d.level_status] || STATUS.error;
  const lvl = typeof d.level === "number" && d.level >= 0 ? d.level : null;
  const up = d.uptime == null ? "—" : d.uptime >= 3600 ? `${Math.floor(d.uptime / 3600)}h` : `${Math.floor(d.uptime / 60)}m`;
  return (
    <div
      className={`group relative bg-white rounded-3xl border border-slate-200/70 shadow-sm p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${s.glow}`}
      data-testid={`cuby-device-${d.device_id}`}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="min-w-0">
          <span className="flex items-center gap-2.5 font-bold text-slate-900 text-2xl">
            <span className="p-2 rounded-xl bg-red-50 text-red-600"><Fuel className="w-5 h-5" /></span>
            <span className="truncate">{d.name}</span>
          </span>
          <p className="text-xs text-slate-400 mt-2 ml-1 font-mono">{d.device_id}{d.model ? ` · Mod. ${d.model}` : ""}</p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <span className={`text-xs font-bold px-3.5 py-1.5 rounded-full ${s.badge}`}>{s.t}</span>
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${d.online ? "text-emerald-600" : "text-slate-400"}`}>
            <span className="relative flex h-2 w-2">
              {d.online && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />}
              <span className={`relative inline-flex rounded-full h-2 w-2 ${d.online ? "bg-emerald-500" : "bg-slate-300"}`} />
            </span>
            {d.online ? "En línea" : "Fuera de línea"}
          </span>
        </div>
      </div>

      <GasGauge level={lvl} ring={s.ring} />

      <div className="flex items-stretch gap-1 mt-6 pt-6 border-t border-slate-100">
        <Metric icon={Thermometer} label="Temp °C" value={d.temperature == null ? "—" : d.temperature} color="text-orange-400" />
        <Metric icon={Droplets} label="Humedad %" value={d.humidity == null ? "—" : d.humidity} color="text-sky-400" />
        <Metric icon={Signal} label="Señal dBm" value={d.rssi == null ? "—" : d.rssi} color="text-violet-400" />
        <Metric icon={Timer} label="Uptime" value={up} color="text-emerald-400" />
      </div>

      <p className="text-xs text-slate-400 mt-5 flex items-center gap-1.5">
        <Clock className="w-4 h-4" />Última lectura · {fmtTs(d.timestamp)}
      </p>
      {d.error && <p className="text-xs text-red-400 mt-1">{d.error}</p>}
    </div>
  );
}

export default function Cuby() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["cuby-dashboard"],
    queryFn: async () => (await api.get("/cuby/dashboard")).data,
    refetchInterval: 60000,
  });
  if (isLoading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 text-red-600 animate-spin" /></div>;
  if (isError) return <p className="text-center text-slate-400 py-32">No se pudo conectar con Cuby.</p>;
  const devices = data?.devices || [];
  const withReading = devices.filter((d) => typeof d.level === "number" && d.level >= 0);
  const avg = withReading.length ? Math.round(withReading.reduce((a, d) => a + d.level, 0) / withReading.length) : null;
  const criticos = devices.filter((d) => d.level_status === "critico").length;
  const online = devices.filter((d) => d.online).length;
  return (
    <div className="w-full space-y-8" data-testid="cuby-dashboard">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-red-950 p-8 shadow-lg">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #ef4444 0, transparent 40%), radial-gradient(circle at 80% 70%, #f59e0b 0, transparent 40%)" }} />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-red-400">
              <Gauge className="w-4 h-4" />Cuby · Monitoreo de Tanques
            </p>
            <h1 className="font-display text-5xl font-extrabold text-white mt-2">Nivel de Gas</h1>
            <p className="text-sm text-slate-300 mt-2 flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-amber-400" />
              {devices.length} {devices.length === 1 ? "dispositivo" : "dispositivos"} · se actualiza cada minuto
            </p>
          </div>
          <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-2xl px-5 py-3 ring-1 ring-white/10">
            <div className="text-center px-3">
              <p className="text-3xl font-extrabold text-white tabular-nums">{avg == null ? "N/D" : `${avg}%`}</p>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">Nivel promedio</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Dispositivos" value={devices.length} icon={Fuel} tone="slate" />
        <Kpi label="En línea" value={online} icon={Wifi} tone="emerald" />
        <Kpi label="Nivel promedio" value={avg == null ? "N/D" : `${avg}%`} icon={Gauge} tone="slate" />
        <Kpi label="En crítico" value={criticos} icon={Thermometer} accent={criticos > 0} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
        {devices.map((d) => <DeviceCard key={d.device_id} d={d} />)}
        {!devices.length && <p className="text-slate-400">No hay dispositivos configurados.</p>}
      </div>
    </div>
  );
}
